import React, { useState, useCallback, useEffect, useMemo } from "react";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";

import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { CUSTOM_CONSTANTS } from "Constants";

// Services
import UserwalletApi from "Services/Wallet";

const UserWallet = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [row, setRow] = useState([]);
  const [bulkrow, setbulkrow] = useState([]);
  const [currentRowData, setCurrentRowData] = useState({});
  const [customerId, setCustomerId] = useState("");
  const [searchText, setSearchText] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  const handlewalletdetails = (currentRowData) => {
    navigate(`/user-wallet-logs/${currentRowData._id}/${currentRowData.userId}`);
  };

  // Get userWallet list api
  const getuserWalletlist = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data || []);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const params = { page, limit: pageSize };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    UserwalletApi.getuserWalletList(params).then(onSuccess, onFailure);
  }, [page, pageSize, demoaccount_id, dispatch]);

  const getalluserWalletlist = useCallback(() => {
    const onSuccess = (res) => {
      if (res?.data?.status === true) {
        setbulkrow(res?.data?.data?.data || []);
      } else {
        setbulkrow([]);
      }
    };
    const onFailure = () => {
      setbulkrow([]);
    };
    const params = { page: 1, limit: "unlimited" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    UserwalletApi.getuserWalletList(params).then(onSuccess, onFailure);
  }, [demoaccount_id]);

  useEffect(() => {
    getuserWalletlist();
    getalluserWalletlist();
  }, [getuserWalletlist]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const dataRows = useMemo(() => {
    let filtered = (row || []).filter((data) => data != null);
    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      filtered = filtered.filter((data) => {
        const uname = (data?.userdetails?.user_name || data?.user_name || "").toLowerCase();
        const mail = (data?.userdetails?.email || data?.email || "").toLowerCase();
        const mob = (data?.userdetails?.mobileNumber || data?.mobileNumber || "").toString();
        return uname.includes(query) || mail.includes(query) || mob.includes(query);
      });
    }
    return _.map(filtered, (data, index) => {
      const uname = data?.userdetails?.user_name || data?.user_name || "User";
      const mail = data?.userdetails?.email || data?.email || "-";
      const code = data?.userdetails?.countryCode || data?.countryCode || "";
      const num = data?.userdetails?.mobileNumber || data?.mobileNumber || "";
      const mob = `${code} ${num}`.trim() || "-";
      const wbal = Number(data?.walletBalance ?? data?.wallet ?? 0) || 0;

      return {
        sno: calculateSno(page, pageSize, index),
        username: uname,
        email: mail,
        mobile: mob,
        wallet: wbal,
        row: data,
      };
    });
  }, [row, page, pageSize, searchText]);

  const formatteddatas = useMemo(() => {
    return dataRows.map((r) => ({
      "S.NO": r.sno,
      "User Name": r.username,
      Email: r.email,
      Mobile: r.mobile,
      Wallet: `${r.wallet.toFixed(2)} ${CUSTOM_CONSTANTS.BASE_CURRENCY}`,
    }));
  }, [dataRows]);

  const bulkdataRows = useMemo(() => {
    return _.map(_.orderBy(bulkrow || [], ["createdAt"], ["asc"]), (data, index) => {
      const uname = data?.userdetails?.user_name || data?.user_name || "User";
      const mail = data?.userdetails?.email || data?.email || "-";
      const code = data?.userdetails?.countryCode || data?.countryCode || "";
      const num = data?.userdetails?.mobileNumber || data?.mobileNumber || "";
      const mob = `${code} ${num}`.trim() || "-";
      const wbal = Number(data?.walletBalance ?? data?.wallet ?? 0) || 0;

      return {
        sno: index + 1,
        username: uname,
        email: mail,
        mobile: mob,
        wallet: wbal,
        row: data,
      };
    });
  }, [bulkrow]);

  const bulkformatteddatas = useMemo(() => {
    return bulkdataRows.map((r) => ({
      "S.NO": r.sno,
      "User Name": r.username,
      Email: r.email,
      Mobile: r.mobile,
      Wallet: r.wallet,
    }));
  }, [bulkdataRows]);

  // Export handlers
  const handleCopy = () => {
    const columnWidths = [5, 15, 25, 15, 15];
    const padText = (text, width) => (text ?? "").toString().padEnd(width, " ");
    const header = [
      padText("S.NO", columnWidths[0]),
      padText("User Name", columnWidths[1]),
      padText("Email", columnWidths[2]),
      padText("Mobile", columnWidths[3]),
      padText("Wallet", columnWidths[4]),
    ].join("");

    const rowsText = dataRows
      .map((r) =>
        [
          padText(r.sno, columnWidths[0]),
          padText(r.username, columnWidths[1]),
          padText(r.email, columnWidths[2]),
          padText(r.mobile, columnWidths[3]),
          padText(r.wallet, columnWidths[4]),
        ].join("")
      )
      .join("\n");

    navigator.clipboard.writeText([header, rowsText].join("\n")).then(() => {
      dispatch(
        showSnackbar({
          message: "Table copied to clipboard!",
          autoHideDuration: 2000,
          variant: "success",
        })
      );
    });
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatteddatas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Wallet");
    XLSX.writeFile(workbook, "Userwallet.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Userwallet.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const tableData = dataRows.map((r) => [r.sno, r.username, r.email, r.mobile, r.wallet]);
    doc.autoTable({
      head: [["S.NO", "User Name", "Email", "Mobile", "Wallet"]],
      body: tableData,
    });
    doc.save("Userwallet.pdf");
  };

  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkformatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All_Userwallet.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Pagination
  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>User Wallet</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>User Wallet</span>
        </Typography>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        {/* Search */}
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search user wallet..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>

        {/* Export Group */}
        <Box className={classes.rightActions}>
          <Box className={classes.exportGroup}>
            <Button className={classes.exportButton} onClick={handleCopy}>
              COPY
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadExcel}>
              EXCEL
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadCSV}>
              CSV
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadPDF}>
              PDF
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadAll}>
              DOWNLOAD ALL
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>Name</TableCell>
              <TableCell className={classes.tableHeaderCell}>Email</TableCell>
              <TableCell className={classes.tableHeaderCell}>Mobile</TableCell>
              <TableCell className={classes.tableHeaderCell}>Wallet</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                <TableCell
                  className={`${classes.tableCell} ${classes.clickableName}`}
                  onClick={() => handlewalletdetails(rowItem.row)}
                >
                  {rowItem.username}
                </TableCell>
                <TableCell className={classes.tableCell}>{rowItem.email}</TableCell>
                <TableCell className={classes.tableCell}>{rowItem.mobile}</TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.wallet.toFixed(2)} {CUSTOM_CONSTANTS.BASE_CURRENCY}
                </TableCell>
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} style={{ textAlign: "center", padding: "40px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No user wallet data found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>
    </Box>
  );
};

export default UserWallet;
